import { useEffect, useState } from "react";
import axios from "axios";
import { BellRing, Send, CheckCircle2, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";

/**
 * Sollecito Wallet Widget
 *
 * Mostra: KPI sollecito (oggi/settimana/mese/tot), tasso conversione
 * post-sollecito, lista clienti pendenti e CTA "Invia tutti".
 *
 * Backend: /api/admin/sollecito/{pending,stats,recent,send-all,send/{id}}
 */
export const SollecitoWidget = () => {
  const [pending, setPending] = useState(null);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [days, setDays] = useState(3);

  const fetchAll = async (daysValue = days) => {
    setLoading(true);
    try {
      const [p, s, r] = await Promise.all([
        axios.get(`${API}/admin/sollecito/pending`, { params: { days: daysValue } }),
        axios.get(`${API}/admin/sollecito/stats`),
        axios.get(`${API}/admin/sollecito/recent`, { params: { limit: 10 } }),
      ]);
      setPending(p.data);
      setStats(s.data);
      setRecent(r.data.items || []);
    } catch (e) {
      setPending(null);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // Auto-refresh every 2 minutes
    const t = setInterval(() => fetchAll(), 120_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendAll = async () => {
    if (!pending || pending.pending_count === 0) return;
    if (!window.confirm(`Inviare ${pending.pending_count} sollecito email ora?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/sollecito/send-all`, null, { params: { days } });
      toast.success(`Sollecito inviati: ${r.data.sent}/${r.data.attempted}${r.data.failed ? ` (${r.data.failed} falliti)` : ""}`);
      await fetchAll(days);
    } catch (e) {
      toast.error("Errore durante l'invio dei sollecito");
    } finally {
      setSending(false);
    }
  };

  const handleSendOne = async (customerId) => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/sollecito/send/${customerId}`);
      toast.success("Sollecito inviato");
      await fetchAll(days);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore invio";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="sollecito-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lojack-clause-500/10 border border-lojack-clause-500/30 flex items-center justify-center">
            <BellRing size={20} className="text-lojack-clause-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Sollecito Wallet</h3>
            <p className="text-gray-500 text-xs">Promemoria automatici · cron giornaliero 09:00 CET</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => { const v = parseInt(e.target.value, 10); setDays(v); fetchAll(v); }}
            className="bg-[#0A0A0A] border border-gray-700 text-gray-300 text-xs rounded-lg px-2 py-1.5"
            data-testid="sollecito-days-select"
          >
            <option value={1}>≥ 1 giorno</option>
            <option value={3}>≥ 3 giorni (default)</option>
            <option value={5}>≥ 5 giorni</option>
            <option value={7}>≥ 7 giorni</option>
            <option value={14}>≥ 14 giorni</option>
          </select>
          <button
            onClick={() => fetchAll(days)}
            className="p-1.5 rounded-lg border border-gray-700 hover:bg-gray-800"
            data-testid="sollecito-refresh"
            title="Aggiorna"
          >
            <RefreshCw size={14} className={`text-gray-400 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* KPI row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <Kpi label="Oggi" value={stats.sent_today} icon={Clock} color="text-blue-400" />
          <Kpi label="7 giorni" value={stats.sent_week} icon={Send} color="text-lojack-blue-400" />
          <Kpi label="30 giorni" value={stats.sent_month} icon={Send} color="text-lojack-blue-400" />
          <Kpi label="Totale" value={stats.sent_total} icon={CheckCircle2} color="text-lojack-clause-400" />
          <Kpi
            label="Conversione"
            value={`${stats.conversion?.rate_pct || 0}%`}
            sub={`${stats.conversion?.signed_after_sollecito || 0}/${stats.conversion?.total_recipients || 0}`}
            icon={CheckCircle2}
            color={stats.conversion?.rate_pct >= 20 ? "text-lojack-blue-400" : "text-gray-400"}
          />
        </div>
      )}

      {/* Pending list */}
      <div className="border-t border-gray-800 pt-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-gray-300 text-sm font-medium">
            In attesa di sollecito
            <span className="ml-2 px-2 py-0.5 rounded-md bg-lojack-clause-500/10 text-lojack-clause-300 text-xs">
              {pending?.pending_count || 0}
            </span>
          </p>
          <button
            onClick={handleSendAll}
            disabled={!pending || pending.pending_count === 0 || sending}
            className="px-3 py-1.5 rounded-lg bg-lojack-red hover:bg-red-700 text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="sollecito-send-all-btn"
          >
            <Send size={12} />
            {sending ? "Invio..." : "Invia tutti"}
          </button>
        </div>

        {pending && pending.pending_count === 0 ? (
          <div className="text-center py-6">
            <CheckCircle2 size={28} className="text-lojack-blue-500 mx-auto mb-2 opacity-50" />
            <p className="text-gray-500 text-xs">Nessun cliente da sollecitare</p>
          </div>
        ) : pending && pending.pending.length > 0 ? (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {pending.pending.slice(0, 20).map((c) => (
              <PendingRow key={c.id} customer={c} onSend={handleSendOne} disabled={sending} />
            ))}
            {pending.pending_count > 20 && (
              <p className="text-gray-600 text-xs text-center pt-2">
                + altri {pending.pending_count - 20} clienti...
              </p>
            )}
          </div>
        ) : null}
      </div>

      {/* Recent sends — audit log */}
      {recent.length > 0 && (
        <div className="border-t border-gray-800 mt-4 pt-4">
          <p className="text-gray-300 text-sm font-medium mb-2">Ultimi sollecito inviati</p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {recent.map((r, idx) => (
              <div key={`${r.customer_id}-${idx}`} className="flex items-center justify-between text-xs py-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 font-mono">{formatDate(r.sent_at)}</span>
                  <span className="text-gray-300">{r.email}</span>
                </div>
                <span className="text-gray-600 uppercase tracking-wider text-[10px]">{r.country}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={12} className={color} />
      <span className="text-gray-500 text-[10px] uppercase tracking-wider">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-gray-600 text-[10px]">{sub}</p>}
  </div>
);

const PendingRow = ({ customer, onSend, disabled }) => {
  const displayName = customer.company_name
    || `${customer.first_name || ""} ${customer.last_name || ""}`.trim()
    || "(senza nome)";
  return (
    <div className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-[#0A0A0A] transition-colors" data-testid={`sollecito-pending-${customer.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-gray-200 text-xs truncate">{displayName}</p>
        <p className="text-gray-500 text-[10px] truncate">{customer.email} · {customer.country}</p>
      </div>
      <button
        onClick={() => onSend(customer.id)}
        disabled={disabled}
        className="p-1.5 rounded-md border border-lojack-clause-500/30 hover:bg-lojack-clause-500/10 text-lojack-clause-400 disabled:opacity-40"
        title="Invia sollecito a questo cliente"
        data-testid={`sollecito-send-${customer.id}`}
      >
        <Send size={11} />
      </button>
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default SollecitoWidget;
